import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, User, ChevronRight, Edit2, Trash2, Store, X, Users, Upload, Loader2, CheckCircle2, AlertCircle, FileText, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { usePermissions } from "../lib/PermissionsContext";
import { RechercheClientGlobale } from "@/components/lmds/RechercheClientGlobale";  // 🧾 Installateurs Phase 5

const API = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const fmt = v => new Intl.NumberFormat("fr-CA",{style:"currency",currency:"CAD"}).format(v||0);

const EMPTY_FORM = { prenom: "", nom: "", email: "", telephone: "", taux: 0.65, notes: "", unites_assignees: [] };

export function LMDSSousTraitants() {
  const navigate = useNavigate();
  const { user, isInstallateur, isProprietaire } = useAuth();
  const { canDo } = usePermissions();
  const canModify = canDo('lmds_modifier');
  // ✅ FIX (2026-04-20): Le propriétaire voit TOUS les installateurs (bypass de la restriction).
  // La permission `lmds_voir_propre_profil_seulement` ne doit s'appliquer qu'aux sous-traitants.
  const voirPropreSeulement = !isProprietaire && canDo('lmds_voir_propre_profil_seulement');
  const [installers, setInstallers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [unitesDisponibles, setUnitesDisponibles] = useState([]); // Pour le sélecteur d'unités
  const year = new Date().getFullYear();

  // Charger les unités disponibles (vehicules travail + rangement est_unite_travail)
  useEffect(() => {
    const loadUnites = async () => {
      try {
        const [vehRes, rangeRes] = await Promise.all([
          axios.get(`${API}/api/vehicules/all`),
          axios.get(`${API}/api/unites-rangement`),
        ]);
        const veh = (vehRes.data?.vehicules || []).filter(v => !v.categorie || v.categorie === 'travail');
        const rang = (rangeRes.data?.unites || []).filter(u => u.est_unite_travail);
        setUnitesDisponibles([
          ...veh.map(v => ({ id: v.id, nom: v.nom, sous_nom: v.marque || '', type: v.type_utilisation })),
          ...rang.map(u => ({ id: u.id, nom: u.numero, sous_nom: u.description || '', type: u.type_utilisation })),
        ]);
      } catch (e) {
        console.error('Erreur chargement unités:', e);
      }
    };
    loadUnites();
  }, []);

  // Import IA state
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importSaving, setImportSaving] = useState(false);
  const fileRef = useRef(null);

  // Access control: installateurss go directly to their own page
  useEffect(() => {
    if (isInstallateur && user?.employee_id) {
      navigate(`/sous-traitants/lmds/${user.employee_id}`, { replace: true });
    }
  }, [isInstallateur, user, navigate]);

  useEffect(() => { if (!isInstallateur) fetchInstallers(); }, [isInstallateur]);

  async function fetchInstallers() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/lmds/installers`);
      const data = res.data;
      setInstallers(data);
      // Fetch yearly stats for each installer
      const statsMap = {};
      await Promise.all(data.map(async (inst) => {
        try {
          const r = await axios.get(`${API}/api/lmds/resume/${inst.id}/${year}`);
          if (r.status === 200) statsMap[inst.id] = r.data;
        } catch (e) {
          console.error(`Erreur stats installateur ${inst.id}:`, e);
        }
      }));
      setStats(statsMap);
    } catch (e) {
      console.error('Erreur chargement stats:', e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  async function saveInstaller() {
    if (!form.prenom || !form.nom) { toast.error("Prénom et nom requis"); return; }
    try {
      if (editId) {
        await axios.put(`${API}/api/lmds/installers/${editId}`, form);
        toast.success("Installateur mis à jour");
      } else {
        await axios.post(`${API}/api/lmds/installers`, form);
        toast.success("Installateur créé");
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      fetchInstallers();
    } catch { toast.error("Erreur de sauvegarde"); }
  }

  async function deleteInstaller(id, name) {
    if (!window.confirm(`Supprimer ${name} ? Cette action supprime aussi tous ses rapports.`)) return;
    await axios.delete(`${API}/api/lmds/installers/${id}`);
    toast.success("Installateur supprimé");
    fetchInstallers();
  }

  function openEdit(inst) {
    setForm({ prenom: inst.prenom, nom: inst.nom, email: inst.email || "", telephone: inst.telephone || "", taux: inst.taux, notes: inst.notes || "", unites_assignees: inst.unites_assignees || [] });
    setEditId(inst.id);
    setShowForm(true);
  }

  async function handleImportFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setShowImport(true);
    setImportData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/lmds/import-rapport`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (r.status !== 200) throw new Error(`Erreur HTTP ${r.status}`);
      const d = r.data;
      setImportData(d);
      toast.success(`Rapport analysé — ${d.installers?.length || 0} installateur(s) détecté(s) ✓`);
    } catch (err) {
      toast.error(`Erreur import: ${err.response?.data?.detail || err.message}`);
      setShowImport(false);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function saveImport() {
    if (!importData) return;
    setImportSaving(true);
    try {
      const r = await axios.post(`${API}/api/lmds/import-rapport-save`, importData);
      if (r.status !== 200) throw new Error(await r.text());
      const d = r.data;
      toast.success(`${d.count} rapport(s) fusionné(s) avec les données existantes`);
      setShowImport(false);
      setImportData(null);
    } catch (err) {
      toast.error(`Erreur sauvegarde: ${err.message}`);
    } finally {
      setImportSaving(false);
    }
  }

  function updateImportLigne(instIdx, ligneIdx, field, value) {
    setImportData(prev => {
      const next = { ...prev, installers: prev.installers.map((inst, i) =>
        i === instIdx ? { ...inst, lignes: inst.lignes.map((l, j) => j === ligneIdx ? { ...l, [field]: value } : l) } : inst
      )};
      return next;
    });
  }

  function removeImportLigne(instIdx, ligneIdx) {
    setImportData(prev => ({
      ...prev,
      installers: prev.installers.map((inst, i) =>
        i === instIdx ? { ...inst, lignes: inst.lignes.filter((_, j) => j !== ligneIdx) } : inst
      ),
    }));
  }

  function addImportLigne(instIdx) {
    const empty = { nom_client: "", del_date: "", store_number: "", order_number: "", servicing_amt: 0, section: "INSTALL ONLY PAY" };
    setImportData(prev => ({
      ...prev,
      installers: prev.installers.map((inst, i) =>
        i === instIdx ? { ...inst, lignes: [...inst.lignes, empty] } : inst
      ),
    }));
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <Store className="w-4 h-4" />
            <span>Sous-traitants</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Département LMDS</h1>
          <p className="text-slate-500 text-sm mt-1">Le Marché du Store — Gestion des installateurs de stores</p>
        </div>
        {canModify && <button onClick={() => fileRef.current?.click()}
          data-testid="import-rapport-btn"
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors">
          <Upload className="w-4 h-4" />
          Import IA Rapport
        </button>}
        <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={handleImportFile} className="hidden" />
      </div>

      {/* 🧾 Phase 5 : recherche globale client — « sans savoir chez quel installateur » */}
      <div data-testid="recherche-client-globale-lmds">
        <RechercheClientGlobale installateurId={null}
          onOuvrir={(lien) => navigate(`/sous-traitants/lmds/${lien.installateur_id}?annee=${lien.annee}&semaine=${lien.semaine}&ligne=${encodeURIComponent(lien.ligne_id)}`)} />
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Installateurs actifs</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {voirPropreSeulement 
              ? (installers.filter(i => i.id === user?.employee_id && i.actif !== false).length)
              : (installers.filter(i => i.actif !== false).length)
            }
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" title={(() => { const c = Object.values(stats).reduce((a, st) => ({ contrats: a.contrats + (st?.ca_brut_composition?.contrats || 0), service_calls: a.service_calls + (st?.ca_brut_composition?.service_calls || 0), km: a.km + (st?.ca_brut_composition?.km || 0), ajustements: a.ajustements + (st?.ca_brut_composition?.ajustements || 0) }), { contrats: 0, service_calls: 0, km: 0, ajustements: 0 }); return `Composition : contrats ${fmt(c.contrats)} + appels de service ${fmt(c.service_calls)} + km ${fmt(c.km)} + ajustements de factures ${fmt(c.ajustements)}`; })()} data-testid="lmds-ca-brut-carte">
          <p className="text-xs text-slate-500 uppercase tracking-wide">CA brut {year} (contrats + appels de service + km, ajusté)</p>
          <p className="text-2xl font-bold text-[#1a237e] mt-1" data-testid="lmds-ca-brut">
            {voirPropreSeulement
              ? fmt(stats[user?.employee_id]?.ca_brut || 0)
              : fmt(Object.values(stats).reduce((s, st) => s + (st?.ca_brut || 0), 0))
            }
          </p>
          <p className="text-xs text-slate-500 mt-1" data-testid="lmds-contrats-installes">Contrats installés : {voirPropreSeulement
              ? fmt(stats[user?.employee_id]?.total_contrats || 0)
              : fmt(Object.values(stats).reduce((s, st) => s + (st?.total_contrats || 0), 0))}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Net payé {year}</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">
            {voirPropreSeulement
              ? fmt(stats[user?.employee_id]?.total_installateur_net || 0)
              : fmt(Object.values(stats).reduce((s, st) => s + (st?.total_installateur_net || 0), 0))
            }
          </p>
        </div>
      </div>

      {/* Installer cards */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]" /></div>
      ) : installers.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Store className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Aucun installateur</p>
          <p className="text-slate-400 text-sm mt-1">Créez votre premier installateur LMDS</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {installers
            .filter(inst => voirPropreSeulement ? inst.id === user?.employee_id : true)
            .map(inst => {
            const st = stats[inst.id] || {};
            const taux_pct = Math.round((inst.taux || 0.65) * 100);
            return (
              <div key={inst.id} data-testid={`installer-card-${inst.id}`}
                className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1a237e] flex items-center justify-center text-white font-semibold">
                        {inst.prenom[0]}{inst.nom[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{inst.prenom} {inst.nom}</p>
                        <p className="text-xs text-slate-500">{taux_pct}% des contrats</p>
                        {(inst.unites_assignees || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(inst.unites_assignees || []).map((u, i) => (
                              <span key={u.id || u._id || i} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <Truck className="w-3 h-3" /> {u.nom_unite || u.unite_id}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {canModify && <div className="flex gap-1">
                      <Link to="/employes" title="Modifier via Employés" className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid={`edit-installer-${inst.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Link>
                    </div>}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between" title={`Composition : contrats ${fmt(st.ca_brut_composition?.contrats || 0)} + appels de service ${fmt(st.ca_brut_composition?.service_calls || 0)} + km ${fmt(st.ca_brut_composition?.km || 0)} + ajustements ${fmt(st.ca_brut_composition?.ajustements || 0)}`}>
                      <span className="text-slate-500">CA brut {year} (contrats + appels de service + km, ajusté)</span>
                      <span className="font-medium" data-testid={`lmds-carte-${inst.id}-ca-brut`}>{fmt(st.ca_brut ?? st.total_contrats)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Contrats installés</span>
                      <span className="text-slate-500" data-testid={`lmds-carte-${inst.id}-contrats`}>{fmt(st.total_contrats)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">CA Installateur</span>
                      <span className="font-medium text-[#1a237e]">{fmt(st.total_installateur_brut)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Net à payer</span>
                      <span className="font-semibold text-emerald-600">{fmt(st.total_installateur_net)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Semaines remplies</span>
                      <span>{st.semaines_remplies || 0} / 52</span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-100 px-5 py-3">
                  <button
                    onClick={() => navigate(`/sous-traitants/lmds/${inst.id}`)}
                    data-testid={`voir-rapports-${inst.id}`}
                    className="w-full flex items-center justify-center gap-2 text-sm text-[#1a237e] font-medium hover:text-[#283593] transition-colors"
                  >
                    Voir les rapports
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="installer-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-semibold text-slate-900">{editId ? "Modifier" : "Nouvel"} installateur</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Prénom *</label>
                  <input value={form.prenom} onChange={e => setForm(p => ({ ...p, prenom: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Nom *</label>
                  <input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Courriel</label>
                <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="installatuer@exemple.com"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Téléphone</label>
                <input value={form.telephone} onChange={e => setForm(p => ({ ...p, telephone: e.target.value }))}
                  placeholder="514-555-0000"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Taux (% des contrats) — ex: 0.65 = 65%, 1.0 = 100%
                </label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" max="1" step="0.05" value={form.taux}
                    onChange={e => setForm(p => ({ ...p, taux: parseFloat(e.target.value) || 0.65 }))}
                    className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  <span className="text-sm text-slate-500 font-medium">{Math.round((form.taux || 0) * 100)}%</span>
                  <button onClick={() => setForm(p => ({ ...p, taux: 0.65 }))} className="text-xs text-[#1a237e] hover:underline">65%</button>
                  <button onClick={() => setForm(p => ({ ...p, taux: 1.0 }))} className="text-xs text-[#1a237e] hover:underline">100%</button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30 resize-none" />
              </div>

              {/* Unités assignées */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1.5 flex items-center gap-1">
                  <Truck className="w-3.5 h-3.5" /> Unités assignées
                </label>
                {/* Unités déjà assignées */}
                <div className="space-y-1 mb-2">
                  {(form.unites_assignees || []).map((u, idx) => (
                    <div key={u.id || u._id || idx} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{u.nom_unite || u.unite_id}</span>
                        <span className="ml-2 text-xs text-slate-400">
                          {u.type_job === 'interieur' ? '🏠' : u.type_job === 'exterieur' ? '🌳' : '🔄'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {['interieur', 'exterieur', 'mixte'].map(t => (
                          <button key={t} type="button"
                            onClick={() => { const up=[...form.unites_assignees]; up[idx]={...up[idx],type_job:t}; setForm(p=>({...p,unites_assignees:up})); }}
                            className={`text-xs px-1.5 py-0.5 rounded border ${u.type_job===t?'bg-indigo-100 border-indigo-400 text-indigo-700 font-semibold':'bg-white border-slate-200 text-slate-400'}`}>
                            {t==='interieur'?'🏠':t==='exterieur'?'🌳':'🔄'}
                          </button>
                        ))}
                        <button type="button" onClick={() => setForm(p=>({...p,unites_assignees:p.unites_assignees.filter((_,i)=>i!==idx)}))} className="ml-1 text-slate-400 hover:text-red-500 text-sm">✕</button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Ajouter une unité */}
                <select
                  value=""
                  onChange={e => {
                    const id = e.target.value;
                    if (!id) return;
                    if ((form.unites_assignees||[]).some(u=>u.unite_id===id)) return;
                    const unite = unitesDisponibles.find(u=>u.id===id);
                    setForm(p=>({...p, unites_assignees: [...(p.unites_assignees||[]), {
                      unite_id: id, nom_unite: unite?.nom||id, type_job: unite?.type||'exterieur'
                    }]}));
                  }}
                  className="w-full border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30"
                >
                  <option value="">+ Ajouter une unité...</option>
                  {unitesDisponibles.filter(u=>!(form.unites_assignees||[]).some(a=>a.unite_id===u.id)).map(u => (
                    <option key={u.id} value={u.id}>{u.nom}{u.sous_nom ? ` — ${u.sous_nom}` : ''}</option>
                  ))}
                  {unitesDisponibles.length === 0 && <option disabled>Aucune unité disponible (créez-en dans la page Unités)</option>}
                </select>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setShowForm(false)} className="flex-1 border border-slate-200 rounded-lg py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Annuler</button>
              <button onClick={saveInstaller} data-testid="save-installer-btn"
                className="flex-1 bg-[#1a237e] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#283593]">
                {editId ? "Mettre à jour" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IMPORT IA MODAL */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h2 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-600" />
                  Import IA — Rapport Hebdomadaire LMDS
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Vérifiez les données extraites avant de sauvegarder. Les service calls sont à ajouter manuellement après.</p>
              </div>
              <button onClick={() => { setShowImport(false); setImportData(null); }} className="p-1.5 hover:bg-slate-100 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {importing && (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
                <p className="text-slate-700 font-medium">Analyse du rapport en cours...</p>
                <p className="text-sm text-slate-400 mt-1">GPT-4o extrait les données de votre rapport</p>
              </div>
            )}

            {importData && !importing && (
              <>
                <div className="p-4 bg-emerald-50 border-b border-emerald-100 flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-emerald-800">
                    Rapport <strong>{importData.rapport_periode}</strong> — Semaine <strong>{importData.semaine}</strong>, {importData.annee} — {importData.installers?.length} installateur(s) détecté(s)
                  </span>
                  <div className="ml-auto flex gap-2">
                    <span className="text-xs text-slate-500">Semaine :</span>
                    <input type="number" min="1" max="52" value={importData.semaine || ""}
                      onChange={e => setImportData(p => ({...p, semaine: parseInt(e.target.value)||p.semaine}))}
                      className="w-14 text-xs border border-slate-200 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                    <span className="text-xs text-slate-500">Année :</span>
                    <input type="number" value={importData.annee || ""}
                      onChange={e => setImportData(p => ({...p, annee: parseInt(e.target.value)||p.annee}))}
                      className="w-20 text-xs border border-slate-200 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30" />
                  </div>
                </div>

                <div className="p-4 space-y-6 max-h-[60vh] overflow-y-auto">
                  {(importData.installers || []).map((inst, instIdx) => (
                    <div key={instIdx} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className={`flex items-center justify-between px-4 py-3 ${inst.installer_id ? "bg-[#1a237e] text-white" : "bg-amber-50 border-b border-amber-200"}`}>
                        <div className="flex items-center gap-3">
                          {inst.installer_id
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                            : <AlertCircle className="w-4 h-4 text-amber-500" />
                          }
                          <span className={`font-semibold ${inst.installer_id ? "text-white" : "text-amber-800"}`}>
                            {inst.installer_code} — {inst.installer_name}
                          </span>
                          {inst.installer_matched_name && (
                            <span className="text-xs text-blue-200">→ {inst.installer_matched_name} ({Math.round((inst.installer_taux||0.65)*100)}%)</span>
                          )}
                          {!inst.installer_id && (
                            <span className="text-xs text-amber-600 font-medium">Non trouvé dans le système</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${inst.installer_id ? "text-blue-200" : "text-amber-600"}`}>
                            {inst.lignes?.length || 0} ligne(s) · Total: {fmt(inst.lignes?.reduce((s,l) => s + parseFloat(l.servicing_amt||0), 0))}
                          </span>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 text-slate-600">
                              <th className="px-3 py-2 text-left">Client</th>
                              <th className="px-2 py-2 text-center">Store#</th>
                              <th className="px-2 py-2 text-center">Order#</th>
                              <th className="px-2 py-2 text-center">Del Date</th>
                              <th className="px-2 py-2 text-right">Montant</th>
                              <th className="px-2 py-2 text-left">Section</th>
                              <th className="w-8 px-1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(inst.lignes || []).map((l, lIdx) => (
                              <tr key={lIdx} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-2 py-1.5">
                                  <input value={l.nom_client||""} onChange={e => updateImportLigne(instIdx, lIdx, "nom_client", e.target.value)}
                                    className="w-full border-0 bg-transparent focus:outline-none text-xs" />
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <input value={l.store_number||""} onChange={e => updateImportLigne(instIdx, lIdx, "store_number", e.target.value)}
                                    className="w-12 text-center border-0 bg-transparent focus:outline-none text-xs" />
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <input value={l.order_number||""} onChange={e => updateImportLigne(instIdx, lIdx, "order_number", e.target.value)}
                                    className="w-20 text-center border-0 bg-transparent focus:outline-none text-xs text-slate-500" />
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <input value={l.del_date||""} onChange={e => updateImportLigne(instIdx, lIdx, "del_date", e.target.value)}
                                    className="w-24 text-center border-0 bg-transparent focus:outline-none text-xs" />
                                </td>
                                <td className="px-2 py-1.5 text-right font-semibold">
                                  <input type="number" step="0.01" value={l.servicing_amt||0} onChange={e => updateImportLigne(instIdx, lIdx, "servicing_amt", parseFloat(e.target.value)||0)}
                                    className="w-20 text-right border-0 bg-transparent focus:outline-none font-semibold text-xs" />
                                </td>
                                <td className="px-2 py-1.5 text-xs text-slate-400">{l.section}</td>
                                <td className="px-1 py-1.5">
                                  <button onClick={() => removeImportLigne(instIdx, lIdx)} className="text-slate-300 hover:text-red-400 transition-colors">
                                    <X className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                        <button onClick={() => addImportLigne(instIdx)}
                          className="text-xs text-[#1a237e] hover:underline flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Ajouter une ligne (service call)
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Total global */}
                  {(importData.installers || []).length > 1 && (() => {
                    const totalLignes = (importData.installers || []).reduce((s, inst) => s + (inst.lignes?.length || 0), 0);
                    const totalMontant = (importData.installers || []).reduce((s, inst) => s + (inst.lignes || []).reduce((ss, l) => ss + parseFloat(l.servicing_amt || 0), 0), 0);
                    return (
                      <div className="bg-[#1a237e] text-white rounded-xl px-5 py-4 flex items-center justify-between" data-testid="import-total-global">
                        <span className="font-bold text-sm">TOTAL GLOBAL</span>
                        <div className="flex items-center gap-6 text-sm">
                          <span>{totalLignes} ligne(s)</span>
                          <span className="font-bold text-lg">{fmt(totalMontant)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex gap-3 p-4 border-t border-slate-100 bg-slate-50">
                  <button onClick={() => { setShowImport(false); setImportData(null); }}
                    className="flex-1 border border-slate-200 rounded-lg py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                    Annuler
                  </button>
                  <button onClick={saveImport} disabled={importSaving} data-testid="confirm-import-btn"
                    className="flex-1 bg-emerald-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                    {importSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sauvegarde...</> : <><CheckCircle2 className="w-4 h-4" /> Confirmer et fusionner</>}
                  </button>
                </div>
                <p className="text-center text-[11px] text-slate-400 pb-3">Les données existantes sont conservées. Seules les nouvelles lignes sont ajoutées.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
